import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from './AuthContext';
import { useErrorMessage } from '../utils/errorMessage';

const PensionerContext = createContext();

export const PensionerContextProvider = ({ children }) => {
  const { dispatchAPI, user } = useAuthContext();
  const { message } = useErrorMessage();
  const navigate = useNavigate();

  const [pensioner, setPensioner] = useState({});
  const [pensionerSupervisions, setPensionerSupervisions] = useState([]);
  const [reviewActiveKey, setReviewActiveKey] = useState('received_reviews');
  const [questionnaireType, setQuestionnaireType] = useState('users:PENSIONER');
  const [veterinarian, setVeterinarian] = useState();
  const [groomer, setGroomer] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const getPensioner = useCallback(async (id) => {
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/pensioners/form/backoffice/${id}`
      });
      setPensioner(data);
    } catch (e) {
      message(e);
    }
    setIsLoading(false);
  }, []);

  const getPensionerSupervisions = useCallback(async (id) => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/supervisions?status=FINISHED&pensioners=${id}&populate=pensioners,main_informations.owner`
      });
      setPensionerSupervisions(data);
    } catch (e) {
      message(e);
    }
  }, []);

  const handleChangeQuestionnaireType = useCallback((key) => {
    setReviewActiveKey(key);
    if (key === 'received_reviews') {
      setQuestionnaireType('users:PENSIONER');
    }
    if (key === 'left_reviews') {
      setQuestionnaireType('users:OWNER');
    }
  }, []);

  const deletePensioner = async (id) => {
    try {
      await dispatchAPI('PATCH', {
        url: `/pensioners/${id}`,
        body: { status: 'ARCHIVED', is_archived: true }
      });
      await dispatchAPI('PATCH', {
        url: `/users/${pensioner.user}`,
        body: { is_archived: true, role: user.role }
      });
      navigate(-1);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const contextValue = useMemo(
    () => ({
      pensioner,
      getPensioner,
      pensionerSupervisions,
      reviewActiveKey,
      setReviewActiveKey,
      questionnaireType,
      setQuestionnaireType,
      veterinarian,
      setVeterinarian,
      groomer,
      setGroomer,
      handleChangeQuestionnaireType,
      isLoading,
      getPensionerSupervisions,
      deletePensioner
    }),
    [
      pensioner,
      getPensioner,
      pensionerSupervisions,
      reviewActiveKey,
      setReviewActiveKey,
      questionnaireType,
      setQuestionnaireType,
      veterinarian,
      setVeterinarian,
      groomer,
      setGroomer,
      handleChangeQuestionnaireType,
      isLoading,
      getPensionerSupervisions,
      deletePensioner
    ]
  );

  return (
    <PensionerContext.Provider value={contextValue}>
      {children}
    </PensionerContext.Provider>
  );
};

export const usePensionerContext = () => useContext(PensionerContext);
